import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import Shell from "../components/Shell";
import { buildSeriesShows, findSeriesShow } from "../series";
import { pickResumeSeason, saveSeriesResume } from "../seriesResume";

function formatDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  if (!total) return "";
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  if (h) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m} min`;
}

/** Nombre limpio + número de capítulo (sin temporada: ya estamos dentro). */
function episodeLabel(video, seriesTitle, index) {
  const number =
    video.episode_number != null && !Number.isNaN(Number(video.episode_number))
      ? Number(video.episode_number)
      : index + 1;

  let name = String(video.title || "").trim();
  // Ya viene "1. Nombre" desde TMDB
  const numbered = name.match(/^(\d+)\.\s+(.+)$/);
  if (numbered) {
    return { number: Number(numbered[1]) || number, title: name };
  }

  name = name.replace(/\s*·\s*T?\d{1,2}\s*[xE]\s*\d{1,3}\s*$/i, "").trim();
  name = name.replace(/\s*[-–]\s*T?\d{1,2}E\d{1,3}\s*$/i, "").trim();
  name = name.replace(/^T?\d{1,2}E\d{1,3}\b\s*[·:\-]?\s*/i, "").trim();
  if (seriesTitle) {
    const re = new RegExp(
      `^${String(seriesTitle).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*[·:\\-]\\s*`,
      "i"
    );
    name = name.replace(re, "").trim();
    if (name.toLowerCase() === seriesTitle.toLowerCase()) name = "";
  }
  name = name.replace(/^T?\d{1,2}E\d{1,3}\b\s*/i, "").trim();
  if (!name || /^cap[ií]tulo\s*\d+$/i.test(name)) {
    return { number, title: `Capítulo ${number}` };
  }
  return { number, title: `${number}. ${name}` };
}

const EPISODES_PER_ROW = 5;

function chunkEpisodes(items, size = EPISODES_PER_ROW) {
  const rows = [];
  for (let i = 0; i < items.length; i += size) {
    rows.push(items.slice(i, i + size));
  }
  return rows;
}

function SeriesPoster({ show }) {
  const years =
    show.years?.length > 1
      ? `${Math.min(...show.years)}–${Math.max(...show.years)}`
      : show.years?.[0]
        ? String(show.years[0])
        : "";

  return (
    <Link to={`/series/${show.slug}`} className="saga-card" draggable={false}>
      <div className="saga-card-media">
        {show.cover_url ? (
          <img src={show.cover_url} alt={show.title} loading="lazy" decoding="async" />
        ) : (
          <div className="poster-fallback">{show.title.slice(0, 1)}</div>
        )}
        <div className="saga-card-overlay">
          <span className="saga-card-count">
            {show.seasonCount} temp. · {show.count} cap.
          </span>
          {years ? <span className="saga-card-years">{years}</span> : null}
        </div>
      </div>
      <span className="saga-card-title">{show.title}</span>
    </Link>
  );
}

function ShowsRow({
  videos,
  includeAnime = false,
  onlyAnime = false,
  title = "Series",
  eyebrow = "Temporadas",
  linkTo = "/series",
}) {
  const shows = useMemo(() => {
    const built = buildSeriesShows(videos, { includeAnime: includeAnime || onlyAnime });
    if (onlyAnime) {
      return built.filter((s) => (s.content_type || "") === "anime_series");
    }
    return built.filter((s) => (s.content_type || "") !== "anime_series");
  }, [videos, includeAnime, onlyAnime]);
  if (!shows.length) return null;
  const visible = shows.slice(0, 9);

  return (
    <section className="row-section row-section-sagas row-section-series is-inview">
      <div className="row-head">
        <div>
          <p className="row-eyebrow">{eyebrow}</p>
          <h2>{title}</h2>
        </div>
        <Link to={linkTo} className="row-more">
          Ver más
        </Link>
      </div>
      <div className="row-slider">
        <div className="poster-row saga-row">
          {visible.map((show) => (
            <SeriesPoster key={show.slug} show={show} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function SeriesRow({ videos }) {
  return (
    <ShowsRow
      videos={videos}
      includeAnime={false}
      title="Series"
      eyebrow="Temporadas"
      linkTo="/series"
    />
  );
}

export function AnimeSeriesRow({ videos }) {
  return (
    <ShowsRow
      videos={videos}
      onlyAnime
      title="Anime (series)"
      eyebrow="Temporadas"
      linkTo="/anime"
    />
  );
}

function EpisodeCard({ video, index, seriesTitle, onOpen }) {
  const rating = video.rating_avg;
  const duration = formatDuration(video.duration_seconds);
  const { title } = episodeLabel(video, seriesTitle, index);
  const href = `/watch/${video.id}`;

  function handleOpen() {
    onOpen?.(video);
  }

  return (
    <article className="series-ep-card" style={{ "--film-i": index }}>
      <Link to={href} className="series-ep-poster" onClick={handleOpen}>
        {video.cover_url ? (
          <img src={video.cover_url} alt="" loading="lazy" decoding="async" />
        ) : (
          <div className="poster-fallback">{title.slice(0, 1)}</div>
        )}
        <span className="saga-film-play" aria-hidden="true">
          <span className="saga-film-play-dot" />
        </span>
      </Link>
      <div className="series-ep-body">
        <h3>
          <Link to={href} onClick={handleOpen}>
            {title}
          </Link>
        </h3>
        <p className="series-ep-meta muted">
          {duration || null}
          {duration && rating ? " · " : null}
          {rating ? `★ ${rating}` : null}
        </p>
      </div>
    </article>
  );
}

function useCatalogVideos() {
  const { token, profile } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api("/api/videos", { token })
      .then((data) => {
        if (!cancelled) setVideos(Array.isArray(data) ? data : data?.videos || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "No se pudo cargar el catálogo");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, profile?.id]);

  return { videos, loading, error };
}

export function SeriesIndexPage() {
  const { videos, loading, error } = useCatalogVideos();
  const shows = useMemo(() => buildSeriesShows(videos), [videos]);

  return (
    <Shell wide>
      <div className="saga-page">
        <section className="saga-hero saga-hero-index">
          <div className="saga-hero-copy">
            <p className="saga-kicker">Catálogo</p>
            <h1>Series</h1>
            <p className="saga-hero-lead">
              Elige una serie, selecciona la temporada y sigue capítulo a capítulo.
            </p>
          </div>
        </section>

        {loading ? <p className="muted catalog-status">Cargando series…</p> : null}
        {error ? <p className="error catalog-status">{error}</p> : null}
        {!loading && !shows.length ? (
          <p className="muted catalog-status">Aún no hay series publicadas.</p>
        ) : null}

        <div className="saga-grid">
          {shows.map((show) => (
            <SeriesPoster key={show.slug} show={show} />
          ))}
        </div>
      </div>
    </Shell>
  );
}

export function SeriesDetailPage() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profile } = useAuth();
  const { videos, loading, error } = useCatalogVideos();
  const show = useMemo(() => findSeriesShow(videos, slug), [videos, slug]);
  const [heroBg, setHeroBg] = useState("");
  const [season, setSeason] = useState(null);
  const resumeInitKey = useRef("");

  function syncSeasonUrl(nextSeason) {
    if (nextSeason == null) return;
    const current = searchParams.get("season");
    if (String(current || "") === String(nextSeason)) return;
    const next = new URLSearchParams(searchParams);
    next.set("season", String(nextSeason));
    setSearchParams(next, { replace: true });
  }

  useEffect(() => {
    if (!show) {
      setHeroBg("");
      setSeason(null);
      resumeInitKey.current = "";
      return;
    }
    const initKey = `${show.slug}:${profile?.id || "anon"}`;
    if (resumeInitKey.current !== initKey) {
      resumeInitKey.current = initKey;
      const querySeason = searchParams.get("season");
      const initial = pickResumeSeason(show, {
        profileId: profile?.id,
        querySeason,
      });
      setSeason(initial);
      if (initial != null) {
        saveSeriesResume(profile?.id, show.slug, { season: initial });
        syncSeasonUrl(initial);
      }
    }
    const coverSeason = season ?? show.seasons?.[0] ?? null;
    const seasonCover =
      (coverSeason != null && show.seasonPosters?.[coverSeason]) ||
      show.cover_url ||
      "";
    setHeroBg(
      show.items.find((v) => v.series_backdrop_url)?.series_backdrop_url ||
        show.backdrop_url ||
        show.items.find((v) => v.backdrop_url)?.backdrop_url ||
        seasonCover ||
        ""
    );
  }, [show, profile?.id, season, searchParams, setSearchParams]);

  function selectSeason(nextSeason) {
    setSeason(nextSeason);
    if (show && nextSeason != null) {
      saveSeriesResume(profile?.id, show.slug, { season: nextSeason });
      syncSeasonUrl(nextSeason);
    }
  }

  function rememberEpisode(video) {
    if (!show || !video) return;
    const sn =
      video.season_number == null || Number.isNaN(Number(video.season_number))
        ? season || 1
        : Number(video.season_number);
    saveSeriesResume(profile?.id, show.slug, {
      season: sn,
      episodeId: video.id,
    });
  }

  const seasonCoverUrl = useMemo(() => {
    if (!show) return "";
    if (season != null && show.seasonPosters?.[season]) {
      return show.seasonPosters[season];
    }
    return show.cover_url || "";
  }, [show, season]);

  const seasonEpisodes = useMemo(() => {
    if (!show) return [];
    if (season == null) return show.items;
    return show.items.filter((ep) => {
      const s =
        ep.season_number == null || Number.isNaN(Number(ep.season_number))
          ? 1
          : Number(ep.season_number);
      return s === Number(season);
    });
  }, [show, season]);

  const episodeRows = useMemo(
    () => chunkEpisodes(seasonEpisodes, EPISODES_PER_ROW),
    [seasonEpisodes]
  );

  const backHref = show?.content_type === "anime_series" ? "/anime" : "/series";

  const synopsis = useMemo(() => {
    if (!show) return "";
    const fromShow = show.items.find((v) => (v.description || "").trim());
    return (fromShow?.description || "").trim();
  }, [show]);

  if (!loading && !error && videos.length && !show) {
    return <Navigate to="/series" replace />;
  }

  const yearLabel =
    show?.years?.length > 1
      ? `${Math.min(...show.years)} – ${Math.max(...show.years)}`
      : show?.years?.[0]
        ? String(show.years[0])
        : "";

  const totalMinutes = Math.round(
    (show?.items || []).reduce((sum, v) => sum + (Number(v.duration_seconds) || 0), 0) /
      60
  );

  return (
    <Shell wide>
      <div className="saga-page">
        <section
          className="saga-hero saga-hero-detail"
          style={heroBg ? { "--saga-bg": `url(${heroBg})` } : undefined}
        >
          <div className="saga-hero-bg" aria-hidden="true" />
          <div className="saga-hero-fade" aria-hidden="true" />
          <div className="saga-hero-inner">
            <div className="saga-hero-copy">
              <Link to={backHref} className="saga-back">
                ← {show?.content_type === "anime_series" ? "Anime" : "Series"}
              </Link>
              <p className="saga-kicker">
                {show?.content_type === "anime_series" ? "Anime · Serie" : "Serie"}
              </p>
              <h1>{show?.title || (loading ? "…" : "Serie")}</h1>
              <p className="saga-hero-lead">
                {show
                  ? `${show.count} capítulo${show.count === 1 ? "" : "s"} · ${
                      show.seasonCount
                    } temporada${show.seasonCount === 1 ? "" : "s"}${
                      yearLabel ? ` · ${yearLabel}` : ""
                    }${totalMinutes ? ` · ~${totalMinutes} min` : ""}`
                  : null}
              </p>

              {synopsis ? <p className="saga-hero-synopsis">{synopsis}</p> : null}

              {show ? (
                <div className="saga-hero-stats">
                  <div>
                    <span>Capítulos</span>
                    <strong>{show.count}</strong>
                  </div>
                  <div>
                    <span>Temporadas</span>
                    <strong>{show.seasonCount}</strong>
                  </div>
                  <div>
                    <span>Periodo</span>
                    <strong>{yearLabel || "—"}</strong>
                  </div>
                  <div>
                    <span>En esta temp.</span>
                    <strong>{seasonEpisodes.length}</strong>
                  </div>
                </div>
              ) : null}

              {show && show.seasons.length ? (
                <label className="series-season-select">
                  <span className="saga-kicker">Temporada</span>
                  <select
                    value={season ?? ""}
                    onChange={(e) =>
                      selectSeason(
                        e.target.value === "" ? null : Number(e.target.value)
                      )
                    }
                  >
                    {show.seasons.map((s) => (
                      <option key={s} value={s}>
                        Temporada {s}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>

            {seasonCoverUrl ? (
              <div className="saga-poster-carousel saga-poster-static" aria-hidden="false">
                <div className="saga-poster-stage">
                  <img
                    key={seasonCoverUrl}
                    src={seasonCoverUrl}
                    alt={
                      season != null
                        ? `${show.title} · Temporada ${season}`
                        : show.title
                    }
                  />
                </div>
              </div>
            ) : null}
          </div>
        </section>

        {loading ? <p className="muted catalog-status">Cargando…</p> : null}
        {error ? <p className="error catalog-status">{error}</p> : null}

        {show ? (
          <section
            className="series-ep-section"
            aria-label={`Capítulos de ${show.title}`}
          >
            {seasonEpisodes.length ? (
              <div className="series-ep-rows">
                {episodeRows.map((row, rowIndex) => (
                  <div
                    key={`row-${rowIndex}`}
                    className="series-ep-row"
                    aria-label={`Fila ${rowIndex + 1}`}
                  >
                    {row.map((video, index) => (
                      <EpisodeCard
                        key={video.id}
                        video={video}
                        index={rowIndex * EPISODES_PER_ROW + index}
                        seriesTitle={show.title}
                        onOpen={rememberEpisode}
                      />
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <p className="muted catalog-status">No hay capítulos en esta temporada.</p>
            )}
          </section>
        ) : null}
      </div>
    </Shell>
  );
}
